import {LocalDate} from '@js-joda/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import {createFilterOptions} from '@material-ui/lab/Autocomplete';
import LoadingButton from '@tracker/shared/src/LoadingIndicator/LoadingButton';
import {Employee, Project} from '@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler';
import {useScheduleContext} from '@tracker/shared/src/Scheduling/Utils/ScheduleContextProvider';
import {Autocomplete, makeValidate} from 'mui-rff';
import React, {useCallback, useMemo, useState} from 'react';
import {Form} from 'react-final-form';
import * as yup from 'yup';
import {IndividualSchedule} from './ProjectScheduleTable';

const useStyles = makeStyles(theme => ({
    cancel: {
        color: theme.palette.grey['500'],
    },
}));

type Props = {
    project : Project;
    schedules : IndividualSchedule[];
    startDate : LocalDate;
};

type FormData = {
    employee : Employee;
};

const filterOptions = createFilterOptions<Employee>({
    limit: 100,
});

const AddEmployeeButton : React.FC<Props> = ({project, schedules, startDate} : Props) => {
    const classes = useStyles();
    const scheduleContext = useScheduleContext();
    const [open, setOpen] = useState(false);

    const [validate, employees] = useMemo(() => {
        const employees = scheduleContext.employees.filter(
            employee => !schedules.some(schedule => schedule.employee.id === employee.id)
        );
        const schema = yup.object({
            employee: yup.mixed<Employee>().label('Employee').required(),
        }).required();
        const validate = makeValidate(schema);

        return [validate, employees];
    }, [scheduleContext.employees, schedules]);

    const handleSubmit = useCallback(async (formData : FormData) => {
        await scheduleContext.addProject(formData.employee.id, project, startDate);
        setOpen(false);
    }, [scheduleContext, project, startDate]);

    return (
        <>
            <Tooltip title="Add employee">
                <IconButton size="small" color="inherit" onClick={() => setOpen(true)}>
                    <AddIcon/>
                </IconButton>
            </Tooltip>

            <Form
                subscription={{submitting: true, pristine: true}}
                onSubmit={handleSubmit}
                validate={validate}
                render={({handleSubmit, pristine, submitting, form: {reset}}) => (
                    <Dialog
                        open={open}
                        onClose={(_event, reason) => {
                            if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                                setOpen(false)
                            }
                        }}
                        disableEscapeKeyDown={submitting}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            component: 'form',
                            onSubmit: handleSubmit,
                            // @ts-ignore Incomplete prop definition
                            noValidate: true,
                        }}
                        TransitionProps={{
                            onEntered: () => {
                                reset()
                            },
                        }}
                    >
                        <DialogTitle>Add Employee</DialogTitle>
                        <DialogContent>
                            <Autocomplete
                                name="employee"
                                label="Employee"
                                options={employees}
                                filterOptions={filterOptions}
                                getOptionLabel={option => {
                                    if (!option) {
                                        return '';
                                    }

                                    return `${option.firstName} ${option.lastName}`;
                                }}
                                textFieldProps={{variant: 'outlined', autoFocus: true}}
                                disableClearable
                                helperText="Search is limited to 100 results"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                                className={classes.cancel}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                color="primary"
                                loading={submitting}
                                disabled={pristine || submitting}
                            >
                                Add
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                )}
            />
        </>
    );
};

export default AddEmployeeButton;
